import { Cut, QrCode } from '@carbon/icons-react';
import { useTranslation } from '@pos/i18n';
import './PrintPreview.scss';

/** One printed line, as the store lays it out (the HO preview endpoint returns these). */
export interface PrintPreviewLine {
  kind: 'text' | 'feed' | 'cut' | 'drawer' | 'qr' | string;
  /** Final text: wrapped and aligned with spaces, so it is shown as is. For a QR code, its data. */
  text: string;
  bold: boolean;
  /** Double width and height: half as many characters on the line. */
  large: boolean;
  /** Empty lines of a feed; dots per module of a QR code. */
  count: number;
}

export interface PrintPreviewProps {
  /** Characters per line: 32 (58 mm) or 48 (80 mm). */
  columns: number;
  lines: readonly PrintPreviewLine[];
  /** Names the paper for screen readers, e.g. "Receipt preview". */
  label: string;
}

/**
 * A receipt printer's paper (FR-CFG-12): monospace at the printer's width, bold and double-size lines as printed, feeds as
 * space, the cut as a dashed line, a QR code as a centred box naming its data (FR-EIN-06). It shows exactly the lines the store would print; nothing is laid out here.
 */
export function PrintPreview({ columns, lines, label }: PrintPreviewProps) {
  const { t } = useTranslation();
  return (
    <article className="pos-print-preview" aria-label={label} style={{ width: `${columns + 2}ch` }}>
      {lines.map((line, index) => {
        const key = `${index}-${line.kind}`;
        switch (line.kind) {
          case 'feed':
            return <div key={key} className="pos-print-preview__feed" style={{ height: `${line.count * 1.5}em` }} />;
          case 'cut':
            return (
              <div key={key} className="pos-print-preview__cut" role="separator" aria-label={t('printTemplates.cut')}>
                <Cut aria-hidden="true" />
              </div>
            );
          case 'qr':
            return (
              <div key={key} className="pos-print-preview__qr">
                <div className="pos-print-preview__qr-code" role="img" aria-label={line.text}>
                  <QrCode size={32} aria-hidden="true" />
                </div>
                <span className="pos-print-preview__qr-data" title={line.text}>
                  {line.text}
                </span>
              </div>
            );
          case 'drawer':
            return (
              <div key={key} className="pos-print-preview__note">
                {t('printTemplates.drawer')}
              </div>
            );
          default:
            return (
              <div
                key={key}
                className={[
                  'pos-print-preview__line',
                  line.bold ? 'pos-print-preview__line--bold' : '',
                  line.large ? 'pos-print-preview__line--large' : '',
                ].join(' ')}
              >
                {line.text || ' '}
              </div>
            );
        }
      })}
    </article>
  );
}
